/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useState } from 'react';

import { useMemoizedFn, useRequest } from 'ahooks';
import {
  type OAuthApp,
  type CreateOAuthAppRequest,
  type CreateOAuthAppResponseData,
  type UpdateOAuthAppRequest,
} from '@coze-arch/bot-api/oauth_permission_api';
import { oauthPermissionApi } from '@coze-arch/bot-api';

export const useGetOAuthAppList = ({ spaceId }: { spaceId?: string }) => {
  const [dataSource, setDataSource] = useState<OAuthApp[]>([]);
  const fetchList = useMemoizedFn(() =>
    oauthPermissionApi.ListOAuthApps({ space_id: spaceId }),
  );
  const { loading, run: fetchData } = useRequest(fetchList, {
    manual: true,
    onSuccess: data => {
      setDataSource(data?.data?.oauth_apps ?? []);
    },
  });
  return { dataSource, loading, fetchData };
};

export const useCreateOAuthApp = () => {
  const [successData, setSuccessData] =
    useState<CreateOAuthAppResponseData>();
  const { loading, run: runCreate } = useRequest(
    (info: CreateOAuthAppRequest) => oauthPermissionApi.CreateOAuthApp(info),
    {
      manual: true,
      onSuccess: data => setSuccessData(data?.data),
    },
  );
  return { runCreate, loading, successData };
};

export const useUpdateOAuthApp = (handle: {
  successHandle?: () => void;
} = {}) => {
  const { loading, run: runUpdate } = useRequest(
    (info: UpdateOAuthAppRequest) => oauthPermissionApi.UpdateOAuthApp(info),
    {
      manual: true,
      onSuccess: () => handle?.successHandle?.(),
    },
  );
  return { runUpdate, loading };
};

export const useDisableOAuthApp = ({
  successHandle,
}: {
  successHandle: () => void;
}) => {
  const { loading, runAsync } = useRequest(
    (id: string) => oauthPermissionApi.DisableOAuthApp({ id }),
    { manual: true },
  );
  const runDisable = async (id: string) => {
    await runAsync(id);
    successHandle();
  };
  return { runDisable, loading };
};

export const useDeleteOAuthApp = ({
  successHandle,
}: {
  successHandle: () => void;
}) => {
  const { loading, runAsync } = useRequest(
    (id: string) => oauthPermissionApi.DeleteOAuthApp({ id }),
    { manual: true },
  );
  const runDelete = async (id: string) => {
    await runAsync(id);
    successHandle();
  };
  return { runDelete, loading };
};
